import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { brMill } from "@react-jvectormap/brazil";

import { fmtBRL, fmtInt } from "./formatters";

const { width: MAP_W, height: MAP_H, paths } = brMill.content;

const EMPTY_METRIC = {
  quantidade: 0,
  quantidadeCte: 0,
  valor: 0,
};

const METRICS = [
  {
    key: "previstoNf.quantidade",
    label: "Previsto NF",
    bucket: "previstoNf",
    field: "quantidade",
    format: "count",
    unitLabel: "NF(s)",
    target: "previsto-nf",
    color: "#1d8cf8",
  },
  {
    key: "realizadoCte.quantidade",
    label: "Realizado CTe",
    bucket: "realizadoCte",
    field: "quantidade",
    format: "count",
    unitLabel: "NF(s)",
    target: "realizado-nf",
    color: "#00bf9a",
  },
  {
    key: "aRealizarNfsCte.quantidade",
    label: "A realizar",
    bucket: "aRealizarNfsCte",
    field: "quantidade",
    format: "count",
    unitLabel: "NF(s)",
    target: "arealizar",
    color: "#ff8d72",
  },
  {
    key: "realizadoFatura.valor",
    label: "Realizado Fatura",
    bucket: "realizadoFatura",
    field: "valor",
    format: "money",
    target: "realizado-fatura",
    color: "#fd5d93",
  },
  {
    key: "aRealizarFatura.quantidadeCte",
    label: "CTe sem Fatura",
    bucket: "aRealizarFatura",
    field: "quantidadeCte",
    format: "count",
    unitLabel: "CTe(s)",
    target: "arealizar-fatura",
    color: "#ff8d72",
  },
];

const VIEW_OPTIONS = [
  { key: "mapa", label: "Mapa UF", icon: "tim-icons icon-square-pin" },
  { key: "ranking", label: "Ranking", icon: "tim-icons icon-chart-bar-32" },
  { key: "esteira", label: "Esteira", icon: "tim-icons icon-align-left-2" },
  { key: "heatmap", label: "Heatmap", icon: "tim-icons icon-components" },
];

const RANKING_GROUPS = [
  { key: "transportadora", label: "Transportadora", nivel: 0 },
  { key: "negocio", label: "Negócio", nivel: 1 },
  { key: "uf", label: "UF", nivel: 2 },
];

const MAP_DIMENSIONS = [
  {
    key: "uf",
    label: "UF",
    pathIndex: 2,
    heading: "Top UFs",
    recorteField: "estadoId",
  },
  {
    key: "negocio",
    label: "Negócio",
    pathIndex: 1,
    heading: "Top negócios",
    recorteField: "negocioId",
  },
  {
    key: "transportadora",
    label: "Transportadora",
    pathIndex: 0,
    heading: "Top transportadoras",
    recorteField: "transportadoraId",
  },
];

const HEATMAP_COLUMNS = [
  {
    key: "previstoNf.quantidade",
    bucket: "previstoNf",
    field: "quantidade",
    label: "Previsto NF",
    target: "previsto-nf",
    format: "count",
  },
  {
    key: "realizadoCte.quantidade",
    bucket: "realizadoCte",
    field: "quantidade",
    label: "NF com CTe",
    target: "realizado-nf",
    format: "count",
  },
  {
    key: "aRealizarNfsCte.quantidade",
    bucket: "aRealizarNfsCte",
    field: "quantidade",
    label: "NF sem CTe",
    target: "arealizar",
    format: "count",
  },
  {
    key: "realizadoFatura.quantidadeCte",
    bucket: "realizadoFatura",
    field: "quantidadeCte",
    label: "CTe com Fat.",
    target: "realizado-fatura-cte",
    format: "count",
  },
  {
    key: "aRealizarFatura.quantidadeCte",
    bucket: "aRealizarFatura",
    field: "quantidadeCte",
    label: "CTe sem Fat.",
    target: "arealizar-fatura",
    format: "count",
  },
];

const getBucket = (node, bucket) =>
  (node.metrics && node.metrics[bucket]) || EMPTY_METRIC;

const getMetricValue = (node, metric) =>
  Number(getBucket(node, metric.bucket)[metric.field] || 0);

const getBucketFieldValue = (node, column) =>
  Number(getBucket(node, column.bucket)[column.field] || 0);

const formatByType = (format, value, unitLabel) => {
  if (format === "money") return fmtBRL(value);
  return `${fmtInt(value)}${unitLabel ? ` ${unitLabel}` : ""}`;
};

const formatMetricValue = (metric, value) =>
  formatByType(metric.format, value, metric.unitLabel);

const formatColumnValue = (column, value) =>
  formatByType(column.format, value, column.unitLabel);

const normalizeLabel = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

const formatFaturaQuantity = (bucket) => {
  const ctes = Number(bucket.quantidadeCte || 0);
  const faturas = Number(bucket.quantidade || 0);
  if (ctes > 0 && faturas > 0) return `${fmtInt(ctes)} CTe(s) / ${fmtInt(faturas)} fatura(s)`;
  if (ctes > 0) return `${fmtInt(ctes)} CTe(s)`;
  return `${fmtInt(faturas)} fatura(s)`;
};

const getFillColor = (ratio) => {
  const clamped = Math.max(0, Math.min(1, ratio || 0));
  return `color-mix(in srgb, var(--faturamento-map-fill-low), var(--faturamento-map-fill-high) ${Math.round(clamped * 100)}%)`;
};

function flattenTree(tree) {
  const out = [];

  const walk = (nodes, path) => {
    nodes.forEach((node) => {
      const nextPath = path.concat(node.label);
      out.push({
        node,
        key: node.key,
        nivel: node.nivel,
        label: node.label,
        path: nextPath,
      });
      if (node.children && node.children.length) walk(node.children, nextPath);
    });
  };

  walk(tree, []);
  return out;
}

function summarizeTree(tree) {
  return HEATMAP_COLUMNS.reduce((acc, column) => {
    if (acc[column.bucket]) return acc;
    acc[column.bucket] = { quantidade: 0, quantidadeCte: 0, valor: 0 };
    tree.forEach((node) => {
      const bucket = getBucket(node, column.bucket);
      acc[column.bucket].quantidade += Number(bucket.quantidade || 0);
      acc[column.bucket].quantidadeCte += Number(bucket.quantidadeCte || 0);
      acc[column.bucket].valor += Number(bucket.valor || 0);
    });
    return acc;
  }, {});
}

function aggregateByLevel(flatNodes, nivel, metric, labelFallback) {
  const grouped = new Map();

  flatNodes
    .filter((item) => item.nivel === nivel)
    .forEach((item) => {
      const value = getMetricValue(item.node, metric);
      if (value <= 0) return;
      const label = normalizeLabel(item.label, labelFallback);
      const key = nivel === 2 ? label.toLowerCase() : label;
      const current = grouped.get(key) || {
        key,
        label,
        value: 0,
        quantity: 0,
        nodes: [],
      };
      current.value += value;
      current.quantity += Number(getBucket(item.node, metric.bucket).quantidade || 0);
      current.nodes.push(item);
      grouped.set(key, current);
    });

  return Array.from(grouped.values()).sort((a, b) => b.value - a.value);
}

const getDimensionValue = (item, dimension) =>
  normalizeLabel(item.path[dimension.pathIndex], `(Sem ${dimension.label})`);

const getUfCode = (item) => normalizeLabel(item.path[2], "").toLowerCase();

const getDimensionRecorteValue = (item, dimension) =>
  item.node && item.node.recorte
    ? item.node.recorte[dimension.recorteField]
    : undefined;

const getDimensionKey = (item, dimension) => {
  const label = getDimensionValue(item, dimension);
  if (dimension.key === "uf") return label.toLowerCase();
  const recorteValue = getDimensionRecorteValue(item, dimension);
  return recorteValue != null
    ? `${dimension.key}:${recorteValue}`
    : `${dimension.key}:${label}`;
};

const getDimensionRecorte = (item, dimension) => {
  const recorteValue = getDimensionRecorteValue(item, dimension);
  return recorteValue != null ? { [dimension.recorteField]: recorteValue } : {};
};

const matchesMapScope = (item, scope) => {
  if (!scope) return true;
  const dimension = MAP_DIMENSIONS.find((option) => option.key === scope.dimension);
  if (!dimension) return true;
  const recorteValue = scope.recorte && scope.recorte[dimension.recorteField];
  if (recorteValue != null) {
    return getDimensionRecorteValue(item, dimension) === recorteValue;
  }
  return getDimensionValue(item, dimension) === scope.value;
};

function getMapBaseNodes(flatNodes) {
  return flatNodes.filter((item) => item.nivel === 2);
}

function aggregateMapDimension(flatNodes, dimension, metric, filters = {}) {
  const { selectedUf, scope, ignoreOwnScope = false } = filters;
  const grouped = new Map();

  getMapBaseNodes(flatNodes).forEach((item) => {
    if (selectedUf && getUfCode(item) !== selectedUf) return;
    if (
      scope &&
      !(ignoreOwnScope && scope.dimension === dimension.key) &&
      !matchesMapScope(item, scope)
    ) {
      return;
    }

    const value = getMetricValue(item.node, metric);
    if (value <= 0) return;

    const label = getDimensionValue(item, dimension);
    const key = getDimensionKey(item, dimension);
    const current = grouped.get(key) || {
      key,
      label,
      value: 0,
      recorte: getDimensionRecorte(item, dimension),
      nodes: [],
      ufs: new Set(),
      negocios: new Set(),
      transportadoras: new Set(),
    };

    current.value += value;
    current.nodes.push(item);
    current.ufs.add(getDimensionValue(item, MAP_DIMENSIONS[0]));
    current.negocios.add(getDimensionValue(item, MAP_DIMENSIONS[1]));
    current.transportadoras.add(getDimensionValue(item, MAP_DIMENSIONS[2]));
    grouped.set(key, current);
  });

  return Array.from(grouped.values()).sort((a, b) => b.value - a.value);
}

function buildSelectedUfRows(flatNodes, metric, selectedUf, scope) {
  return getMapBaseNodes(flatNodes)
    .filter((item) => getUfCode(item) === selectedUf)
    .filter((item) => matchesMapScope(item, scope))
    .map((item) => ({
      key: item.key,
      label: `${item.path[0]} / ${item.path[1]}`,
      helper: "Transportadora / negócio",
      value: getMetricValue(item.node, metric),
      recorte: item.node.recorte,
      nodes: [item],
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
}

function getMapRowHelper(row, dimension, selectedUf) {
  if (dimension.key === "uf") {
    return `${fmtInt(row.nodes.length)} recorte(s)`;
  }

  if (dimension.key === "negocio") {
    return selectedUf
      ? `${fmtInt(row.transportadoras.size)} transportadora(s)`
      : `${fmtInt(row.ufs.size)} UF(s)`;
  }

  return selectedUf
    ? `${fmtInt(row.negocios.size)} negócio(s)`
    : `${fmtInt(row.ufs.size)} UF(s)`;
}

function buildFlowStages(summary) {
  const nfsPrevistas = Number(summary.previstoNf.quantidade || 0);
  const nfsRealizadasCte = Number(summary.realizadoCte.quantidade || 0);
  const nfsPendentesCte = Math.max(0, nfsPrevistas - nfsRealizadasCte);
  const ctesRealizados = Number(summary.realizadoCte.quantidadeCte || 0);
  const ctesEmFatura = Number(summary.realizadoFatura.quantidadeCte || 0);
  const ctesSemFatura = Number(summary.aRealizarFatura.quantidadeCte || 0);

  return [
    {
      key: "previstoNf",
      label: "Previsto NF",
      caption: "Base do recorte",
      displayValue: `${fmtInt(nfsPrevistas)} NF(s)`,
      progressValue: nfsPrevistas,
      progressBase: nfsPrevistas,
      progressLabel: "base operacional",
      quantityLabel: "no recorte",
      target: "previsto-nf",
      iconClassName: "tim-icons icon-notes",
      color: "#1d8cf8",
    },
    {
      key: "realizadoCte",
      label: "Realizado CTe",
      caption: "NF com CTe",
      displayValue: `${fmtInt(nfsRealizadasCte)} NF(s)`,
      progressValue: nfsRealizadasCte,
      progressBase: nfsPrevistas,
      progressLabel: "das NF previstas",
      quantityLabel: `${fmtInt(ctesRealizados)} CTe(s)`,
      target: "realizado-nf",
      iconClassName: "tim-icons icon-delivery-fast",
      color: "#00bf9a",
    },
    {
      key: "aRealizarNfsCte",
      label: "A realizar CTe",
      caption: "Pendente de CTe",
      displayValue: `${fmtInt(nfsPendentesCte)} NF(s)`,
      progressValue: nfsPendentesCte,
      progressBase: nfsPrevistas,
      progressLabel: "das NF previstas",
      quantityLabel: "sem CTe",
      target: "arealizar",
      iconClassName: "tim-icons icon-time-alarm",
      color: "#ff8d72",
    },
    {
      key: "realizadoFatura",
      label: "Realizado Fatura",
      caption: "CTe com fatura ativa",
      displayValue: fmtBRL(summary.realizadoFatura.valor),
      progressValue: ctesEmFatura,
      progressBase: ctesRealizados,
      progressLabel: "dos CTes realizados",
      quantityLabel: formatFaturaQuantity(summary.realizadoFatura),
      target: "realizado-fatura",
      iconClassName: "tim-icons icon-check-2",
      color: "#00bf9a",
    },
    {
      key: "aRealizarFatura",
      label: "A realizar Fatura",
      caption: "CTe sem fatura ativa",
      displayValue: fmtBRL(summary.aRealizarFatura.valor),
      progressValue: ctesSemFatura,
      progressBase: ctesRealizados,
      progressLabel: "dos CTes realizados",
      quantityLabel: `${fmtInt(ctesSemFatura)} CTe(s) sem fatura ativa`,
      target: "arealizar-fatura",
      iconClassName: "tim-icons icon-time-alarm",
      color: "#fd5d93",
    },
  ];
}

function MetricSelector({ activeMetric, onChange }) {
  return (
    <div className="faturamento-visual-metric-tabs" role="group" aria-label="Metrica">
      {METRICS.map((metric) => (
        <button
          key={metric.key}
          type="button"
          className={`faturamento-visual-chip ${
            activeMetric.key === metric.key ? "is-active" : ""
          }`}
          onClick={() => onChange(metric)}
        >
          <span
            className="faturamento-visual-chip__dot"
            style={{ backgroundColor: metric.color }}
          />
          {metric.label}
        </button>
      ))}
    </div>
  );
}

function BrazilRevenueMap({ ufData, selectedUf, onSelectUf, metric }) {
  const [tooltip, setTooltip] = useState(null);
  const valueByUf = useMemo(() => {
    const values = {};
    ufData.forEach((item) => {
      values[item.key] = item.value;
    });
    return values;
  }, [ufData]);
  const maxValue = ufData.length ? Math.max.apply(null, ufData.map((item) => item.value)) : 0;
  const minValue = ufData.length ? Math.min.apply(null, ufData.map((item) => item.value)) : 0;

  return (
    <div className="faturamento-visual-map-shell">
      <svg
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        className="faturamento-visual-map"
        onMouseMove={(event) => {
          const code = event.target.getAttribute("data-code");
          if (!code || !paths[code]) {
            setTooltip(null);
            return;
          }
          setTooltip({
            x: event.clientX,
            y: event.clientY,
            name: paths[code].name,
            value: valueByUf[code],
          });
        }}
        onMouseLeave={() => setTooltip(null)}
      >
        {Object.entries(paths).map(([code, state]) => {
          const value = valueByUf[code];
          const hasValue = Number(value || 0) > 0;
          const isSelected = selectedUf === code;
          const opacity = selectedUf && !isSelected ? 0.36 : 1;
          const fill = isSelected
            ? "var(--faturamento-map-selected-fill)"
            : hasValue
            ? getFillColor(maxValue > 0 ? value / maxValue : 0)
            : "var(--faturamento-map-empty-fill)";

          return (
            <path
              key={code}
              data-code={code}
              d={state.path}
              fill={fill}
              opacity={opacity}
              stroke={
                isSelected
                  ? "var(--faturamento-map-selected-stroke)"
                  : "var(--faturamento-map-stroke)"
              }
              strokeWidth={isSelected ? 650 : 220}
              className={hasValue ? "is-clickable" : ""}
              onClick={() => {
                if (hasValue) onSelectUf(isSelected ? null : code);
              }}
            />
          );
        })}
      </svg>

      <div className="faturamento-visual-map-legend">
        <span>{formatMetricValue(metric, minValue)}</span>
        <div />
        <span>{formatMetricValue(metric, maxValue)}</span>
      </div>

      {tooltip && (
        <div
          className="faturamento-visual-tooltip"
          style={{ left: tooltip.x + 12, top: tooltip.y - 38 }}
        >
          <strong>{tooltip.name}</strong>
          <span>
            {Number(tooltip.value || 0) > 0
              ? formatMetricValue(metric, tooltip.value)
              : "Sem dados"}
          </span>
          <small>{metric.label}</small>
        </div>
      )}
    </div>
  );
}

function FaturamentoMapaView({
  flatNodes,
  selectedUf,
  onSelectUf,
  metric,
  onDrilldown,
  onDocumentContextChange,
}) {
  const [activeDimension, setActiveDimension] = useState(MAP_DIMENSIONS[0]);
  const [mapScope, setMapScope] = useState(null);
  const ufDimension = MAP_DIMENSIONS[0];

  const ufData = useMemo(
    () =>
      aggregateMapDimension(flatNodes, ufDimension, metric, {
        scope: mapScope,
      }),
    [flatNodes, metric, mapScope, ufDimension]
  );

  const selected = selectedUf
    ? ufData.find((item) => item.key === selectedUf)
    : null;

  useEffect(() => {
    if (selectedUf && !selected) onSelectUf(null);
  }, [onSelectUf, selected, selectedUf]);

  const detailRows = useMemo(() => {
    if (activeDimension.key === "uf" && selectedUf) {
      return buildSelectedUfRows(flatNodes, metric, selectedUf, mapScope).slice(0, 8);
    }

    return aggregateMapDimension(flatNodes, activeDimension, metric, {
      selectedUf,
      scope: mapScope,
      ignoreOwnScope: true,
    }).slice(0, 8);
  }, [activeDimension, flatNodes, mapScope, metric, selectedUf]);

  const panelTitle = selectedUf
    ? activeDimension.key === "uf"
      ? "UF selecionada"
      : `${activeDimension.label} na UF`
    : activeDimension.heading;

  const panelStrong = selectedUf
    ? selectedUf.toUpperCase()
    : `${detailRows.length} item(ns)`;

  const documentContext = useMemo(() => {
    const recorte = {
      ...(selected && selected.recorte ? selected.recorte : {}),
      ...(mapScope && mapScope.recorte ? mapScope.recorte : {}),
    };
    const labelParts = [metric.label];
    if (selectedUf) labelParts.push(`UF ${selectedUf.toUpperCase()}`);
    if (mapScope) labelParts.push(mapScope.label);

    return {
      target: metric.target,
      label: labelParts.join(" — "),
      recorte,
    };
  }, [mapScope, metric, selected, selectedUf]);

  useEffect(() => {
    if (onDocumentContextChange && documentContext.target) {
      onDocumentContextChange(documentContext);
    }
  }, [documentContext, onDocumentContextChange]);

  const handleDimensionChange = (dimension) => {
    setActiveDimension(dimension);
  };

  const handleRowSelect = (row) => {
    if (activeDimension.key === "uf") {
      if (!selectedUf && row.key) onSelectUf(row.key);
      return;
    }

    setMapScope((prev) =>
      prev &&
      prev.dimension === activeDimension.key &&
      prev.key === row.key
        ? null
        : {
            dimension: activeDimension.key,
            key: row.key,
            value: row.label,
            label: `${activeDimension.label}: ${row.label}`,
            recorte: row.recorte || {},
          }
    );
  };

  return (
    <div className="faturamento-visual-map-grid">
      <BrazilRevenueMap
        ufData={ufData}
        selectedUf={selectedUf}
        onSelectUf={onSelectUf}
        metric={metric}
      />

      <div className="faturamento-visual-side-panel">
        <div className="faturamento-visual-panel-heading">
          <span>{panelTitle}</span>
          <strong>{panelStrong}</strong>
        </div>

        <div className="faturamento-visual-subtabs faturamento-visual-map-dimensions">
          {MAP_DIMENSIONS.map((dimension) => (
            <button
              key={dimension.key}
              type="button"
              className={activeDimension.key === dimension.key ? "is-active" : ""}
              onClick={() => handleDimensionChange(dimension)}
            >
              {dimension.label}
            </button>
          ))}
        </div>

        {mapScope && (
          <button
            type="button"
            className="faturamento-map-scope"
            onClick={() => setMapScope(null)}
          >
            <span>{mapScope.label}</span>
            <i className="tim-icons icon-simple-remove" />
          </button>
        )}

        <div className="faturamento-visual-list">
          {detailRows.length === 0 && (
            <div className="faturamento-visual-empty-state">
              Sem dados para a métrica selecionada.
            </div>
          )}

          {detailRows.map((item) => {
            const isScopeActive =
              mapScope &&
              mapScope.dimension === activeDimension.key &&
              mapScope.key === item.key;
            const value = item.value;
            const helper =
              item.helper || getMapRowHelper(item, activeDimension, selectedUf);
            const drillNode =
              item.nodes.length === 1 && item.nodes[0] && item.nodes[0].node;
            return (
              <div
                className={`faturamento-visual-list-row ${
                  isScopeActive ? "is-active" : ""
                } ${activeDimension.key !== "uf" || !selectedUf ? "is-selectable" : ""}`}
                key={item.key}
                onClick={() => handleRowSelect(item)}
              >
                <div>
                  <strong>
                    {activeDimension.key === "uf" && !selectedUf
                      ? item.label.toUpperCase()
                      : item.label}
                  </strong>
                  <span>{helper}</span>
                </div>
                <div className="faturamento-visual-list-row__value">
                  <strong>{formatMetricValue(metric, value)}</strong>
                  {drillNode && metric.target && value > 0 && (
                    <Button
                      className="faturamento-button-secondary faturamento-button-secondary--xs"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDrilldown({
                          node: drillNode,
                          target: metric.target,
                          label: metric.label,
                        });
                      }}
                    >
                      Detalhar
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FaturamentoRankingView({ flatNodes, metric, onDrilldown }) {
  const [group, setGroup] = useState(RANKING_GROUPS[0]);
  const ranking = useMemo(
    () => aggregateByLevel(flatNodes, group.nivel, metric, "(Sem nome)").slice(0, 10),
    [flatNodes, group, metric]
  );
  const maxValue = ranking.length ? Math.max.apply(null, ranking.map((item) => item.value)) : 0;

  return (
    <div>
      <div className="faturamento-visual-subtabs" role="group" aria-label="Agrupamento">
        {RANKING_GROUPS.map((option) => (
          <button
            key={option.key}
            type="button"
            className={group.key === option.key ? "is-active" : ""}
            onClick={() => setGroup(option)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="faturamento-ranking-bars">
        {ranking.map((item, index) => {
          const width = maxValue > 0 ? Math.max(6, (item.value / maxValue) * 100) : 0;
          const drillNode =
            item.nodes.length === 1 && item.nodes[0] && item.nodes[0].node;
          return (
            <div className="faturamento-ranking-row" key={item.key}>
              <div className="faturamento-ranking-row__label">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{item.label}</strong>
              </div>
              <div className="faturamento-ranking-row__track">
                <div
                  className="faturamento-ranking-row__bar"
                  style={{ width: `${width}%`, backgroundColor: metric.color }}
                />
              </div>
              <div className="faturamento-ranking-row__value">
                <strong>{formatMetricValue(metric, item.value)}</strong>
                {drillNode && metric.target && item.value > 0 && (
                  <Button
                    className="faturamento-button-secondary faturamento-button-secondary--xs"
                    onClick={() =>
                      onDrilldown({
                        node: drillNode,
                        target: metric.target,
                        label: metric.label,
                      })
                    }
                  >
                    Detalhar
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FaturamentoEsteiraView({ tree, summary, onDrilldown }) {
  const stages = useMemo(() => buildFlowStages(summary), [summary]);
  const globalNode = useMemo(
    () => ({ label: "Todos os recortes", recorte: {}, nivel: 0 }),
    []
  );
  const nfsPrevistas = Number(summary.previstoNf.quantidade || 0);
  const nfsRealizadasCte = Number(summary.realizadoCte.quantidade || 0);
  const nfsPendentesCte = Math.max(0, nfsPrevistas - nfsRealizadasCte);
  const ctesRealizados = Number(summary.realizadoCte.quantidadeCte || 0);
  const ctesEmFatura = Number(summary.realizadoFatura.quantidadeCte || 0);
  const ctesSemFatura = Number(summary.aRealizarFatura.quantidadeCte || 0);

  return (
    <div className="faturamento-pipeline">
      <div className="faturamento-pipeline-summary">
        <div>
          <span>NF previstas</span>
          <strong>{fmtInt(nfsPrevistas)}</strong>
          <small>base operacional</small>
        </div>
        <div>
          <span>NF com CTe</span>
          <strong>{fmtInt(nfsRealizadasCte)}</strong>
          <small>{fmtInt(ctesRealizados)} CTe(s)</small>
        </div>
        <div>
          <span>NF sem CTe</span>
          <strong>{fmtInt(nfsPendentesCte)}</strong>
          <small>pendentes de realizacao</small>
        </div>
        <div>
          <span>CTes sem fatura</span>
          <strong>{fmtInt(ctesSemFatura)}</strong>
          <small>{fmtInt(ctesEmFatura)} CTe(s) com fatura ativa</small>
        </div>
      </div>

      <div className="faturamento-pipeline-grid">
        {stages.map((stage) => {
          const relativePercent =
            stage.progressBase > 0
              ? (stage.progressValue / stage.progressBase) * 100
              : 0;
          const width =
            stage.progressBase > 0 && stage.progressValue > 0
              ? Math.max(
                  4,
                  Math.min(100, (stage.progressValue / stage.progressBase) * 100)
                )
              : 0;
          return (
            <div className="faturamento-pipeline-stage" key={stage.key}>
              <div className="faturamento-pipeline-stage__header">
                <span
                  className="faturamento-pipeline-stage__icon"
                  style={{ color: stage.color }}
                >
                  <i className={stage.iconClassName} />
                </span>
                <div>
                  <strong>{stage.label}</strong>
                  <small>{stage.caption}</small>
                </div>
              </div>

              <div className="faturamento-pipeline-stage__value">
                {stage.displayValue}
              </div>

              <div className="faturamento-pipeline-stage__meter">
                <div
                  style={{
                    width: `${width}%`,
                    backgroundColor: stage.color,
                  }}
                />
              </div>

              <div className="faturamento-pipeline-stage__meta">
                <span>{stage.quantityLabel}</span>
                <strong>
                  {formatPercent(relativePercent)} {stage.progressLabel}
                </strong>
              </div>

              {tree.length > 0 && stage.progressValue > 0 && (
                <Button
                  className="faturamento-button-secondary faturamento-button-secondary--xs"
                  onClick={() =>
                    onDrilldown({
                      node: globalNode,
                      target: stage.target,
                      label: stage.label,
                    })
                  }
                >
                  Ver documentos
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FaturamentoHeatmapView({ tree, onDrilldown }) {
  const rows = useMemo(() => {
    return tree
      .map((node) => ({
        node,
        label: node.label,
        total: getBucketFieldValue(node, HEATMAP_COLUMNS[0]),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 9);
  }, [tree]);
  const maxByColumn = rows.reduce((acc, row) => {
    HEATMAP_COLUMNS.forEach((column) => {
      acc[column.key] = Math.max(
        acc[column.key] || 0,
        getBucketFieldValue(row.node, column)
      );
    });
    return acc;
  }, {});

  return (
    <div className="faturamento-heatmap-wrapper">
      <table className="faturamento-heatmap-table">
        <thead>
          <tr>
            <th>Transportadora</th>
            {HEATMAP_COLUMNS.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.node.key}>
              <td>{row.label}</td>
              {HEATMAP_COLUMNS.map((column) => {
                const value = getBucketFieldValue(row.node, column);
                const columnMax = maxByColumn[column.key] || 0;
                const ratio = columnMax > 0 ? value / columnMax : 0;
                return (
                  <td key={column.key}>
                    <button
                      type="button"
                      className="faturamento-heatmap-cell"
                      style={{
                        "--faturamento-heatmap-mix": `${Math.round(ratio * 100)}%`,
                      }}
                      disabled={!value}
                      title={`${row.label} - ${column.label}: ${formatColumnValue(
                        column,
                        value
                      )}`}
                      onClick={() =>
                        onDrilldown({
                          node: row.node,
                          target: column.target,
                          label: column.label,
                        })
                      }
                    >
                      {formatColumnValue(column, value)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FaturamentoVisualExperiments({
  tree,
  onDrilldown,
  onDocumentContextChange,
}) {
  const [activeView, setActiveView] = useState(VIEW_OPTIONS[0].key);
  const [activeMetric, setActiveMetric] = useState(METRICS[1]);
  const [selectedUf, setSelectedUf] = useState(null);

  const flatNodes = useMemo(() => flattenTree(tree), [tree]);
  const summary = useMemo(() => summarizeTree(tree), [tree]);

  const renderActiveView = () => {
    if (activeView === "ranking") {
      return (
        <FaturamentoRankingView
          flatNodes={flatNodes}
          metric={activeMetric}
          onDrilldown={onDrilldown}
        />
      );
    }

    if (activeView === "esteira") {
      return (
        <FaturamentoEsteiraView
          tree={tree}
          summary={summary}
          onDrilldown={onDrilldown}
        />
      );
    }

    if (activeView === "heatmap") {
      return <FaturamentoHeatmapView tree={tree} onDrilldown={onDrilldown} />;
    }

    return (
      <FaturamentoMapaView
        flatNodes={flatNodes}
        selectedUf={selectedUf}
        onSelectUf={setSelectedUf}
        metric={activeMetric}
        onDrilldown={onDrilldown}
        onDocumentContextChange={onDocumentContextChange}
      />
    );
  };

  return (
    <Card className="card-everlog-shell faturamento-visual-card">
      <CardHeader className="faturamento-card-header faturamento-visual-card__header">
        <div>
          <div className="faturamento-card-eyebrow">Visões do faturamento</div>
          <CardTitle tag="h4" className="mb-1">
            Mapa, ranking, esteira e heatmap
          </CardTitle>
          <small className="faturamento-muted-text">
            Use o mesmo retorno da consulta para navegar pelas quatro visões do
            recorte.
          </small>
        </div>
        <div className="faturamento-visual-tabs" role="group" aria-label="Visualizacao">
          {VIEW_OPTIONS.map((view) => (
            <button
              key={view.key}
              type="button"
              className={activeView === view.key ? "is-active" : ""}
              onClick={() => setActiveView(view.key)}
            >
              <i className={view.icon} />
              {view.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardBody>
        {activeView !== "esteira" && activeView !== "heatmap" && (
          <MetricSelector activeMetric={activeMetric} onChange={setActiveMetric} />
        )}
        <div className="faturamento-visual-stage">{renderActiveView()}</div>
      </CardBody>
    </Card>
  );
}

export default React.memo(FaturamentoVisualExperiments);
